import { useEffect, useState } from "react";
import AdminHeader from "../../components/layout/AdminHeader";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Save, Lock } from "lucide-react";

const GENERAL = [
  { key: "region", label: "Region" },
  { key: "currency", label: "Currency" },
  { key: "languages", label: "Primary languages" },
  { key: "time_zone", label: "Time zone" },
];
const INVESTMENT = [
  { key: "investment_notes", label: "Investment overview", multi: true },
  { key: "buying_process", label: "Buying process", multi: true },
  { key: "required_documents", label: "Required documents", multi: true },
  { key: "ownership_considerations", label: "Ownership considerations", multi: true },
  { key: "typical_opportunity_types", label: "Typical opportunity types", multi: true },
  { key: "market_observations", label: "Market notes", multi: true },
  { key: "common_questions", label: "Frequently asked investor questions", multi: true },
];
const INTERNAL = [
  { key: "internal_contacts", label: "Internal contacts", multi: true },
  { key: "government_links", label: "Government links", multi: true },
  { key: "embassy_info", label: "Embassy information", multi: true },
  { key: "travel_notes", label: "Travel notes", multi: true },
  { key: "risk_notes", label: "Risk notes", multi: true },
  { key: "compliance_notes", label: "Compliance notes", multi: true },
  { key: "internal_team_notes", label: "Internal team notes", multi: true },
  { key: "tax_reminders", label: "Tax reminders", multi: true },
  { key: "legal_reminders", label: "Legal reminders", multi: true },
  { key: "residency_notes", label: "Residency notes", multi: true },
];
const FIELDS = [...GENERAL, ...INVESTMENT, ...INTERNAL];
const VISIBILITY_OPTIONS = [
  { value: "internal_only", label: "Internal Only", hint: "Never exposed to AURI or public." },
  { value: "auri_approved", label: "AURI Approved", hint: "AURI may reference in visitor conversations." },
  { value: "public_approved", label: "Public Approved", hint: "May appear on public pages (future)." },
];

export default function AdminCountryIntel() {
  const [countries, setCountries] = useState([]);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const loadList = async () => {
    const { data } = await api.get("/country-intel");
    setCountries(data);
    if (data.length && !active) { setActive(data[0].country_key); setForm(data[0]); }
  };
  useEffect(() => { loadList(); /* eslint-disable-next-line */ }, []);

  const selectCountry = async (k) => {
    setActive(k);
    const { data } = await api.get(`/country-intel/${k}`);
    setForm(data);
  };

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const patch = Object.fromEntries(FIELDS.map((f) => [f.key, form[f.key] ?? null]));
      patch.visibility_status = form.visibility_status || "internal_only";
      await api.patch(`/country-intel/${active}`, patch);
      toast.success("Saved");
      const { data } = await api.get(`/country-intel/${active}`); setForm(data);
    } catch { toast.error("Failed"); } finally { setSaving(false); }
  };

  return (
    <div data-testid="page-admin-country-intel">
      <AdminHeader breadcrumb="Knowledge · Internal Only" title="Country Intelligence"
        subtitle="Private per-market knowledge base. Never publicly exposed. Later, AURI may reference approved portions."
        actions={<span className="inline-flex items-center gap-2 text-xs text-altura-burgundy"><Lock className="h-3.5 w-3.5" /> Internal only</span>} />
      <div className="p-8 grid gap-6 lg:grid-cols-4">
        <aside className="space-y-1">
          {countries.map((c) => (
            <button key={c.country_key} onClick={() => selectCountry(c.country_key)}
                    className={`w-full text-left rounded-md px-3 py-2.5 text-sm transition ${active === c.country_key ? "bg-altura-espresso text-altura-ivory" : "bg-white border border-altura-espresso/10 text-altura-espresso hover:border-altura-gold"}`}
                    data-testid={`country-tab-${c.country_key}`}>
              <div className="font-medium">{c.country_name}</div>
              {c.last_reviewed_at && <div className={`text-[10px] ${active === c.country_key ? "text-altura-ivory/60" : "text-altura-charcoal/50"}`}>Reviewed {new Date(c.last_reviewed_at).toLocaleDateString()}</div>}
            </button>
          ))}
        </aside>
        <div className="lg:col-span-3 admin-card space-y-4">
          <div className="flex items-center justify-between border-b border-altura-espresso/10 pb-4">
            <div>
              <div className="font-editorial text-2xl text-altura-espresso">{form.country_name || "Select a country"}</div>
              {form.last_reviewed_at && <div className="text-xs text-altura-charcoal/55 mt-1">Last reviewed by {form.last_reviewed_by || "—"} on {new Date(form.last_reviewed_at).toLocaleString()}</div>}
            </div>
            <button onClick={save} disabled={saving || !active} className="btn-gold disabled:opacity-60" data-testid="country-save"><Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save"}</button>
          </div>

          <div className="rounded-md border border-altura-espresso/10 bg-altura-ivory-2/60 p-4">
            <div className="text-[11px] uppercase tracking-[0.2em] text-altura-charcoal/60 mb-2">Visibility & AURI safety gate</div>
            <div className="flex flex-wrap gap-2">
              {VISIBILITY_OPTIONS.map((v) => {
                const selected = (form.visibility_status || "internal_only") === v.value;
                return (
                  <button key={v.value} onClick={() => setForm({ ...form, visibility_status: v.value })}
                          className={`rounded-full px-4 py-1.5 text-xs uppercase tracking-widest transition ${selected ? "bg-altura-espresso text-altura-ivory" : "border border-altura-espresso/15 text-altura-espresso hover:border-altura-gold"}`}
                          data-testid={`country-visibility-${v.value}`}>{v.label}</button>
                );
              })}
            </div>
            <div className="mt-2 text-xs text-altura-charcoal/60">
              {(VISIBILITY_OPTIONS.find((v) => v.value === (form.visibility_status || "internal_only")) || {}).hint}
            </div>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-altura-gold-dark mb-3">General information</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {GENERAL.map((f) => (
                <div key={f.key}>
                  <label className="text-[11px] uppercase tracking-[0.2em] text-altura-charcoal/60">{f.label}</label>
                  <input className="admin-input mt-1" value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`country-field-${f.key}`} />
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-altura-gold-dark mb-3 mt-6">Investment information</div>
            <div className="grid grid-cols-1 gap-4">
              {INVESTMENT.map((f) => (
                <div key={f.key}>
                  <label className="text-[11px] uppercase tracking-[0.2em] text-altura-charcoal/60">{f.label}</label>
                  <textarea rows={4} className="admin-input mt-1" value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`country-field-${f.key}`} />
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-altura-burgundy mb-3 mt-6">Internal reference (always internal)</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {INTERNAL.map((f) => (
                <div key={f.key} className={f.multi ? "md:col-span-2" : ""}>
                  <label className="text-[11px] uppercase tracking-[0.2em] text-altura-charcoal/60">{f.label}</label>
                  <textarea rows={3} className="admin-input mt-1" value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`country-field-${f.key}`} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
